import { SvelteMap } from "svelte/reactivity";
import type { FilterState } from "~/lib/filters/types";
import type { BloomreachDoc, BloomreachFacetCounts, BloomreachResponse } from "./types";

// ── Pure utilities ────────────────────────────────────────────────────────────

export function computePriceMax(docs: BloomreachDoc[]): number | undefined {
  const prices = docs.map((d) => d.price_range?.[1] ?? d.price ?? 0).filter((p) => p > 0);
  const max = prices.length > 0 ? Math.max(...prices) : 0;
  return max > 0 ? max : undefined;
}

export function computePriceMin(docs: BloomreachDoc[]): number | undefined {
  const prices = docs.map((d) => d.price_range?.[0] ?? d.price ?? 0).filter((p) => p > 0);
  return prices.length > 0 ? Math.min(...prices) : undefined;
}

// stats.field=price returns a number_stats facet with the true min/max over the
// current (fq-scoped) result set — a better source than doc-derived values, which
// only reflect whatever docs have loaded into the page cache so far. Falls back to
// {} (caller then uses doc-derived values) if the facet is missing for any reason.
export function computePriceFacetStats(facetCounts: BloomreachFacetCounts | undefined): {
  min?: number;
  max?: number;
} {
  const stats = facetCounts?.facets.find(
    (f): f is Extract<typeof f, { type: "number_stats" }> =>
      f.type === "number_stats" && f.name.toLowerCase().includes("price"),
  );
  return stats ? { min: stats.value.start, max: stats.value.end } : {};
}

// Seeded by plp-prefetch.ts once its always-unfiltered prefetch resolves, regardless
// of whether that response is adopted as page-1 data. Without this, a page load that
// starts with a price filter already active (from a shared/bookmarked URL) never sees
// an unfiltered read at all — the composable's own query is fq-scoped from its very
// first response, so createCachedPriceMin/Max would otherwise ratchet to exactly the
// user's own selection, trapping them inside their own filter. Module-level singleton:
// safe because each full page load re-evaluates plp-prefetch.ts fresh, and Search pages
// have no prefetch, so this simply stays undefined for them.
let prefetchedPriceBounds = $state<{ min?: number; max?: number } | undefined>(undefined);

export function setPrefetchedPriceBounds(bounds: { min?: number; max?: number }): void {
  prefetchedPriceBounds = bounds;
}

export function extractFacetOrder(facetCounts: BloomreachFacetCounts): SvelteMap<string, string[]> {
  const order = new SvelteMap<string, string[]>();
  for (const facet of facetCounts.facets) {
    if (facet.type === "text") {
      order.set(
        facet.name,
        facet.value.map((v) => v.name),
      );
    }
  }
  return order;
}

// ── Reactive factories ────────────────────────────────────────────────────────

interface QuerySnapshot {
  isFetching: boolean;
  isPlaceholderData: boolean;
  data: { pages: BloomreachResponse[] } | undefined;
}

// Returns `live ?? preserved`: prefers the live derived value when defined,
// falls back to the last defined value when the derived returns undefined.
// Not suitable when the live value can be defined but wrong (e.g. a lower price
// ceiling after filtering) — use a bespoke preserved state in that case.
function createCachedValue<T>(compute: () => T | undefined): { readonly current: T | undefined } {
  const current = $derived.by(compute);
  let preserved = $state<T | undefined>(undefined);

  $effect.pre(() => {
    if (current !== undefined) preserved = current;
  });

  return {
    get current() {
      return current ?? preserved;
    },
  };
}

const EMPTY_FACET_ORDER = new SvelteMap<string, string[]>();

/**
 * Derives the price ceiling for the range slider from actual product prices in
 * the current response. The ceiling only ever increases — narrowing a filter
 * cannot shrink it — so the slider stays stable as the user adjusts the range.
 *
 * The active price filter's upper bound is used as a floor so that the ceiling
 * is always at least as large as the selected value. Without this, the slider
 * would snap the thumb down when the rounded product-price max falls below the
 * selected value, triggering the "full-range means no filter" removal logic.
 *
 * Intentionally does not use createCachedValue: the live derived can be defined
 * but lower than the preserved ceiling (e.g. after a filter narrows results).
 * createCachedValue returns `live ?? preserved` and would expose the lower live
 * value. Here we always return `preserved` and only update it when live increases.
 */
export function createCachedPriceMax(
  getQuery: () => QuerySnapshot,
  getActiveFilters: () => FilterState,
): { readonly current: number | undefined } {
  const live = $derived.by(() => {
    const q = getQuery();
    if (q.isFetching || q.isPlaceholderData || !q.data) return undefined;
    const docs = q.data.pages.flatMap((p) => p.response.docs);
    const rawMax =
      computePriceFacetStats(q.data.pages[0]?.facet_counts).max ?? computePriceMax(docs);
    const filterUpperBound = (getActiveFilters()["price"] as [number, number] | undefined)?.[1];
    if (rawMax === undefined) return filterUpperBound;
    if (filterUpperBound !== undefined && filterUpperBound > rawMax) return filterUpperBound;
    return rawMax;
  });

  let preserved = $state<number | undefined>(undefined);

  $effect.pre(() => {
    if (live !== undefined && (preserved === undefined || live > preserved)) {
      preserved = live;
    }
  });

  // See prefetchedPriceBounds above — covers cold reload with an active price filter.
  $effect.pre(() => {
    const prefetchedMax = prefetchedPriceBounds?.max;
    if (prefetchedMax !== undefined && (preserved === undefined || prefetchedMax > preserved)) {
      preserved = prefetchedMax;
    }
  });

  return {
    get current() {
      return preserved;
    },
  };
}

/**
 * Mirror of createCachedPriceMax for the slider's lower bound: the ceiling only ever
 * grows, this floor only ever shrinks — and is capped (not floored) at the active
 * price filter's lower bound, so the track never excludes the user's own selection.
 *
 * Sourced from stats.field=price when present (the true, but fq-scoped, catalog min),
 * falling back to the min product price seen in loaded docs otherwise. Confirmed live
 * (2026-07-14) that the stats block narrows under both unrelated and self filters —
 * same reason createCachedPriceMax can't just trust a single live read.
 */
export function createCachedPriceMin(
  getQuery: () => QuerySnapshot,
  getActiveFilters: () => FilterState,
): { readonly current: number | undefined } {
  const live = $derived.by(() => {
    const q = getQuery();
    if (q.isFetching || q.isPlaceholderData || !q.data) return undefined;
    const docs = q.data.pages.flatMap((p) => p.response.docs);
    const rawMin =
      computePriceFacetStats(q.data.pages[0]?.facet_counts).min ?? computePriceMin(docs);
    const filterLowerBound = (getActiveFilters()["price"] as [number, number] | undefined)?.[0];
    if (rawMin === undefined) return filterLowerBound;
    if (filterLowerBound !== undefined && filterLowerBound < rawMin) return filterLowerBound;
    return rawMin;
  });

  let preserved = $state<number | undefined>(undefined);

  $effect.pre(() => {
    if (live !== undefined && (preserved === undefined || live < preserved)) {
      preserved = live;
    }
  });

  // See prefetchedPriceBounds above — covers cold reload with an active price filter.
  $effect.pre(() => {
    const prefetchedMin = prefetchedPriceBounds?.min;
    if (prefetchedMin !== undefined && (preserved === undefined || prefetchedMin < preserved)) {
      preserved = prefetchedMin;
    }
  });

  return {
    get current() {
      return preserved;
    },
  };
}

/**
 * Derives the stable facet option order from the first unfiltered result,
 * preserved while filters are active or a fetch is in progress.
 */
export function createCachedFacetOrder(
  getQuery: () => QuerySnapshot,
  getActiveFilters: () => FilterState,
): { readonly current: Map<string, string[]> } {
  const inner = createCachedValue(() => {
    const q = getQuery();
    if (q.isFetching || q.isPlaceholderData || !q.data) return undefined;
    if (Object.keys(getActiveFilters()).length !== 0) return undefined;
    const facetCounts = q.data.pages[0]?.facet_counts;
    if (!facetCounts) return undefined;
    return extractFacetOrder(facetCounts);
  });

  return {
    get current() {
      return inner.current ?? EMPTY_FACET_ORDER;
    },
  };
}
